import { Navigate, Route, Routes } from 'react-router-dom'
import { AuthProvider, useAuth } from './auth/AuthProvider'
import { usePageview } from './lib/analytics'
import { Layout } from './components/Layout'
import { Home } from './pages/Home'
import { Login } from './pages/Login'
import { AuthCallback } from './pages/AuthCallback'
import { Groups } from './pages/Groups'
import { JoinGroup } from './pages/JoinGroup'
import { Draft } from './pages/Draft'
import { Leaderboard } from './pages/Leaderboard'
import { Marathon } from './pages/Marathon'
import { Stryktips } from './pages/Stryktips'
import { StryktipsFacit } from './pages/StryktipsFacit'
import { AdminTournaments } from './pages/AdminTournaments'
import { AdminTournament } from './pages/AdminTournament'
import { AdminCodes } from './pages/AdminCodes'
import { AdminUsers } from './pages/AdminUsers'
import { AdminGroups } from './pages/AdminGroups'
import { AdminWinners } from './pages/AdminWinners'
import { Nickname } from './pages/Nickname'
import { PublicBoard } from './pages/PublicBoard'
import { Submissions } from './pages/Submissions'
import { Draftboard } from './pages/Draftboard'

function RequireAuth({ children }: { children: JSX.Element }) {
  const { session, profile, loading } = useAuth()

  if (loading) {
    return (
      <div className="page">
        <p className="muted">Laddar…</p>
      </div>
    )
  }

  if (!session) return <Navigate to="/logga-in" replace />

  // Smeknamnet är det som visas i den publika tabellen, så det sätts innan
  // man kommer vidare. Grinden ligger här och inte på en enskild sida — annars
  // hade en bokmärkt djuplänk gått förbi den.
  if (profile && !profile.nickname) return <Nickname />

  return children
}

function Routing() {
  const { session, loading } = useAuth()
  usePageview()

  return (
    <Routes>
      <Route element={<Layout />}>
        <Route
          path="/logga-in"
          element={!loading && session ? <Navigate to="/" replace /> : <Login />}
        />
        <Route path="/auth/callback" element={<AuthCallback />} />

        {/* Publik. Ingen RequireAuth — hela poängen är att den går att skicka. */}
        <Route path="/tabell" element={<PublicBoard />} />
        <Route path="/tabell/:season" element={<PublicBoard />} />

        <Route
          path="/smeknamn"
          element={
            <RequireAuth>
              <Nickname />
            </RequireAuth>
          }
        />

        <Route
          path="/"
          element={
            <RequireAuth>
              <Home />
            </RequireAuth>
          }
        />
        <Route
          path="/gang"
          element={
            <RequireAuth>
              <Groups />
            </RequireAuth>
          }
        />
        <Route
          path="/ga-med/:token"
          element={
            <RequireAuth>
              <JoinGroup />
            </RequireAuth>
          }
        />
        <Route
          path="/turnering/:id/lag"
          element={
            <RequireAuth>
              <Draft />
            </RequireAuth>
          }
        />
        <Route
          path="/turnering/:id/inlamnade"
          element={
            <RequireAuth>
              <Submissions />
            </RequireAuth>
          }
        />
        <Route
          path="/turnering/:id/draftboard"
          element={
            <RequireAuth>
              <Draftboard />
            </RequireAuth>
          }
        />
        <Route
          path="/turnering/:id/leaderboard"
          element={
            <RequireAuth>
              <Leaderboard />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth>
              <AdminTournaments />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/turnering/:id"
          element={
            <RequireAuth>
              <AdminTournament />
            </RequireAuth>
          }
        />
        <Route
          path="/turnering/:id/stryktips"
          element={
            <RequireAuth>
              <Stryktips />
            </RequireAuth>
          }
        />
        <Route
          path="/turnering/:id/stryktips/facit"
          element={
            <RequireAuth>
              <StryktipsFacit />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/koder"
          element={
            <RequireAuth>
              <AdminCodes />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/anvandare"
          element={
            <RequireAuth>
              <AdminUsers />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/gang"
          element={
            <RequireAuth>
              <AdminGroups />
            </RequireAuth>
          }
        />
        <Route
          path="/admin/segrare"
          element={
            <RequireAuth>
              <AdminWinners />
            </RequireAuth>
          }
        />
        <Route
          path="/maraton"
          element={
            <RequireAuth>
              <Marathon />
            </RequireAuth>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  )
}

export function App() {
  return (
    <AuthProvider>
      <Routing />
    </AuthProvider>
  )
}
